// @ts-nocheck — ported 1:1 from React Native provisioning-cli; runtime-validated
import { LACPAggregate, WebFilterConfig, OutboundServicesConfig, WANConfig, ManagementNetworkConfig, SmartPoliciesConfig, AppReachabilityConfig, APP_DEFINITIONS, getAppDefinition } from '../state/wizardStore';
import { IOT_FQDN_GROUPS, ALL_IOT_FQDNS, IOT_TCP_PORT_RANGES, IOT_UDP_PORT_RANGES, getIoTVlans } from '../utils/iotCloudConfig';
import { getModelByName, HardwareModel } from '../hardware/hardwareDictionary';

// ============================================
// SMART POLICIES CONSTANTS
// ============================================

// ============================================
// VLAN Keyword Matching for Smart Policy Resolution
// Updated to match the new Opinionated UI Preset names (case-insensitive)
// ============================================
const VLAN_KEYWORDS = {
  // 'management' preset: Network device management
  admin: ['management', 'admin', 'amministrazione', 'mgmt'],
  
  // 'staff' preset: Employee workstations
  staff: ['staff', 'client', 'dipendenti', 'ufficio', 'office', 'corporate'],
  
  // 'guest' preset: Visitor internet access
  guest: ['guest', 'ospiti', 'visitatori', 'hotspot'],
  
  // 'printers' preset: Network printers (note: preset is 'printers' plural)
  printer: ['printers', 'printer', 'print', 'stampanti', 'stampa', 'mfp'],
  
  // 'voip' preset: IP phones
  voip: ['voip', 'telefoni', 'phone', 'sip', 'pbx'],
  
  // 'iot' preset: Smart devices
  iot: ['iot', 'domotica', 'smart', 'automation', 'cameras'],
  
  // 'ot' preset: HVAC, BMS, SCADA
  ot: ['ot', 'hvac', 'bms', 'scada', 'industrial', 'plc', 'factory'],
  
  // 'servers' preset: On-premises servers
  servers: ['servers', 'server', 'datacenter', 'vm'],
  
  // 'dmz' preset: Public-facing services
  dmz: ['dmz', 'web', 'public'],
  
  // 'blackhole' preset: Quarantine
  blackhole: ['blackhole', 'quarantine', 'blocked'],
};

/**
 * Find VLANs matching specific keywords in their names
 */
function findVlansByKeywords(vlans: VLANInput[], keywords: string[]): VLANInput[] {
  return vlans.filter(vlan => {
    const nameLower = vlan.nome_vlan.toLowerCase();
    return keywords.some(kw => nameLower.includes(kw));
  });
}

/**
 * Find first VLAN matching specific keywords
 */
function findVlanByKeywords(vlans: VLANInput[], keywords: string[]): VLANInput | undefined {
  return vlans.find(vlan => {
    const nameLower = vlan.nome_vlan.toLowerCase();
    return keywords.some(kw => nameLower.includes(kw));
  });
}

interface VLANInput {
  nome_vlan: string;
  vlan_id: string;
  network_ip: string;
  gateway_ip: string;
  subnet_mask: string;
  enable_dhcp: boolean;
  dhcp_start?: string;
  dhcp_end?: string;
  dns_primary?: string;
  dns_secondary?: string;
  porte_assegnate: string[];
  vlan_type?: 'corporate' | 'guest' | 'server' | 'management' | 'iot';
}

interface CLIGeneratorInput {
  hostname: string;
  timezone_id: string;
  password_admin: string;
  dns_primary: string;
  dns_secondary: string;
  destroy_switch: boolean;
  remove_dhcp: boolean;
  disable_fortilink: boolean;
  modello: string;
  
  // Management Port
  mgmt_port?: string;
  mgmt_ip?: string;
  mgmt_subnet?: string;
  
  // LACP Aggregates
  lacp_aggregates?: LACPAggregate[];
  
  // Native Management Network (Untagged on Parent Interface)
  managementNetwork?: ManagementNetworkConfig;
  
  // Policies
  policies: string[];
  policy_config: {
    ad_source_vlan?: string;
    ad_dest_vlan?: string;
    guest_vlan?: string;
    corporate_vlans?: string[];
  };
  
  // WebFilter & UTM
  webfilter_config?: WebFilterConfig;
  outbound_services?: OutboundServicesConfig;
  
  // VLANs
  vlans: VLANInput[];
  
  // NEW: Dynamic WAN Configuration
  wan1?: WANConfig;
  wan2?: WANConfig;
  
  // Smart Policies Configuration
  smart_policies?: SmartPoliciesConfig;
  
  // App Reachability (L7/FQDN/ISDB)
  app_reachability?: AppReachabilityConfig;
  
  // Legacy WAN Configuration (backward compatibility)
  include_sdwan?: boolean;
  wan1_type?: 'dhcp' | 'static' | 'pppoe';
  wan1_ip?: string;
  wan1_gateway?: string;
  wan1_subnet?: string;
  wan1_username?: string;
  wan1_password?: string;
  wan2_enabled?: boolean;
  wan2_type?: 'dhcp' | 'static' | 'pppoe';
  wan2_ip?: string;
  wan2_gateway?: string;
  wan2_subnet?: string;
  wan2_username?: string;
  wan2_password?: string;
}

// Helper: Get the interface name for a WAN config
function getWanInterfaceName(wan: WANConfig, wanNumber: number): string {
  if (wan.is_vlan && wan.vlan_id) {
    return `WAN${wanNumber}_${wan.vlan_id}`;
  }
  return wan.physical_port || `wan${wanNumber}`;
}

// ============================================
// SECURITY-FIRST: Interface Access Control Tiers
// ============================================
// CRITICAL: This function determines what management access each interface gets.
// Exposing SSH/HTTPS to untrusted networks is a critical security vulnerability!

type VLANSecurityTier = 'management' | 'trusted' | 'untrusted';

/**
 * Determines the security tier for a VLAN based on its type/usage
 * @param vlanType - The VLAN type from wizardStore (corporate, guest, server, iot, management)
 * @param vlanName - The VLAN name (used for keyword matching as fallback)
 */
function getVlanSecurityTier(vlanType?: string, vlanName?: string): VLANSecurityTier {
  const nameLower = (vlanName || '').toLowerCase();
  const typeLower = (vlanType || '').toLowerCase();
  
  // TIER 1 - Management: Full access (ping, https, ssh, http, fgfm)
  // Only for dedicated management interfaces
  if (typeLower === 'management' || 
      nameLower.includes('management') || 
      nameLower.includes('mgmt') ||
      nameLower.includes('admin')) {
    return 'management';
  }
  
  // TIER 2 - Trusted: Limited access (ping only for connectivity testing)
  // For internal corporate networks that need basic monitoring
  if (typeLower === 'corporate' || 
      typeLower === 'server' ||
      nameLower.includes('staff') ||
      nameLower.includes('server') ||
      nameLower.includes('datacenter') ||
      nameLower.includes('voip') ||
      nameLower.includes('dmz')) {
    return 'trusted';
  }
  
  // TIER 3 - Untrusted: NO ACCESS (completely locked down)
  // Guest, IoT, Printers, OT, Blackhole - HIGH RISK networks
  // These MUST NOT have any access to firewall management!
  return 'untrusted';
}

/**
 * Returns the Fortinet CLI `allowaccess` string based on security tier
 * @param tier - The security tier of the interface
 * @param isNativeManagement - True if this is the native management network interface
 */
function getFortinetAllowAccess(tier: VLANSecurityTier, isNativeManagement: boolean = false): string {
  if (isNativeManagement) {
    // Native Management Network gets full access
    return '    set allowaccess ping https ssh http fgfm\n';
  }
  
  switch (tier) {
    case 'management':
      // Management VLAN - full access but without FGFM (only on dedicated mgmt)
      return '    set allowaccess ping https ssh\n';
    case 'trusted':
      // Trusted networks - ping only for monitoring/connectivity
      return '    set allowaccess ping\n';
    case 'untrusted':
      // Untrusted networks - NO ACCESS AT ALL
      // Return empty string to omit the line entirely
      return '';
  }
}

export const generateCLIScript = (input: CLIGeneratorInput): string => {
  let script = '';
  
  // BLOCK 1: Initial Setup
  // CRITICAL: This block MUST ONLY contain system global, admin, and DNS.
  // Interface configuration is FORBIDDEN here because:
  // 1. Physical ports may still be bound to fortilink aggregate
  // 2. Hardware switches haven't been dismantled yet
  // 3. DHCP servers haven't been purged yet
  // Management interface is configured in BLOCK 3b AFTER Greenfield Purge.
  script += '# ============================================\n';
  script += '# BLOCK 1: Initial Setup (System Only)\n';
  script += '# ============================================\n';
  script += '# NOTE: Interface config moved to BLOCK 3b (after Greenfield Purge)\n\n';
  script += 'config system global\n';
  script += `  set hostname ${input.hostname}\n`;
  script += `  set timezone ${input.timezone_id}\n`;
  script += 'end\n\n';
  script += 'config system admin\n';
  script += '  edit "admin"\n';
  script += `    set password ${input.password_admin}\n`;
  script += '  next\n';
  script += 'end\n\n';
  
  // DNS Configuration
  if (input.dns_primary || input.dns_secondary) {
    script += 'config system dns\n';
    if (input.dns_primary) {
      script += `  set primary ${input.dns_primary}\n`;
    }
    if (input.dns_secondary) {
      script += `  set secondary ${input.dns_secondary}\n`;
    }
    script += 'end\n\n';
  }
  
  // ============================================
  // BLOCK 2: Greenfield Purge (MANDATORY for ZTP)
  // ============================================
  // CRITICAL EXECUTION ORDER:
  // FortiOS WILL REJECT interface IP changes if factory DHCP servers are still bound.
  // This block MUST execute BEFORE any interface configuration (WAN, VLAN, Management).
  // The purge is fault-tolerant - if objects don't exist, FortiOS ignores the error.
  
  script += '# ============================================\n';
  script += '# BLOCK 2: Greenfield Purge (Zero-Touch Provisioning)\n';
  script += '# ============================================\n';
  script += '# CRITICAL: This block must execute BEFORE any interface configuration\n';
  script += '# FortiOS rejects IP changes on interfaces with bound DHCP servers\n\n';
  
  // Step 1: Purge all firewall policies (if enabled)
  if (input.destroy_switch || input.remove_dhcp || input.disable_fortilink) {
    script += '# Step 1: Purge factory firewall policies\n';
    script += 'config firewall policy\n';
    script += '  purge\n';
    script += 'end\n\n';
  }
  
  // Step 2: ALWAYS delete factory DHCP servers (fault-tolerant)
  // Even if remove_dhcp is false, we MUST clear DHCP to allow IP assignment
  script += '# Step 2: Delete factory DHCP servers (IDs 1-5)\n';
  script += '# NOTE: Fault-tolerant - errors ignored if IDs don\'t exist\n';
  script += 'config system dhcp server\n';
  script += '  delete 1\n';
  script += '  delete 2\n';
  script += '  delete 3\n';
  script += '  delete 4\n';
  script += '  delete 5\n';
  script += 'end\n\n';
  
  // Step 3: Disable FortiLink if requested
  if (input.disable_fortilink) {
    const defaultSwitch = getDefaultSwitchName(input.modello);
    script += '# Step 3: Disable FortiLink and remove switch\n';
    script += 'config system interface\n';
    script += '  edit "fortilink"\n';
    script += '    set member ""\n';
    script += '  next\n';
    script += 'end\n\n';
    script += 'config system virtual-switch\n';
    script += `  delete "${defaultSwitch}"\n`;
    script += 'end\n\n';
  }
  
  // ============================================
  // HAL: Dynamic Hardware Switch Dismantling (Fault-Tolerant)
  // ============================================
  // CRITICAL: Desktop FortiGate models (30G, 40F, 60F, etc.) ship with
  // physical LAN ports grouped in a hardware switch (e.g., "lan" or "internal").
  // These ports CANNOT be assigned VLANs/IPs until extracted from the switch.
  // 
  // FAULT TOLERANCE: If the switch doesn't exist (standalone ports),
  // FortiOS will throw a benign "entry not found" error and continue.
  // This makes the script safe for both factory and post-config states.
  // ============================================
  const hardwareModel = getModelByName(input.modello);
  if (hardwareModel?.hasHardwareSwitch && 
      hardwareModel?.hardwareSwitchPorts && 
      hardwareModel.hardwareSwitchPorts.length > 0) {
    const switchName = hardwareModel.hardwareSwitchName || 'internal';
    
    script += '# ============================================\n';
    script += '# HAL: Hardware Switch Dismantling (Fault-Tolerant)\n';
    script += `# Model: ${hardwareModel.displayName}\n`;
    script += `# Factory Switch: "${switchName}"\n`;
    script += `# Ports to Extract: ${hardwareModel.hardwareSwitchPorts.join(', ')}\n`;
    script += '# NOTE: If switch doesn\'t exist, errors are safely ignored.\n';
    script += '# ============================================\n\n';
    
    // Step 4a: Remove all ports from the hardware switch
    script += '# Step 4a: Extract physical ports from hardware switch\n';
    script += 'config system virtual-switch\n';
    script += `  edit "${switchName}"\n`;
    script += '    config port\n';
    for (const port of hardwareModel.hardwareSwitchPorts) {
      script += `      delete "${port}"\n`;
    }
    script += '    end\n';
    script += '  next\n';
    script += 'end\n\n';
    
    // Step 4b: Delete the now-empty hardware switch
    script += '# Step 4b: Delete the empty hardware switch\n';
    script += 'config system virtual-switch\n';
    script += `  delete "${switchName}"\n`;
    script += 'end\n\n';
    
    // Step 4c: Reset each extracted port to standalone mode
    script += '# Step 4c: Reset extracted ports to standalone interface mode\n';
    script += 'config system interface\n';
    for (const port of hardwareModel.hardwareSwitchPorts) {
      script += `  edit "${port}"\n`;
      script += '    set vdom "root"\n';
      script += '    set mode static\n';
      script += '    set role lan\n';
      script += '  next\n';
    }
    script += 'end\n\n';
  }
  
  script += '# ============================================\n';
  script += '# END GREENFIELD PURGE - Interface config can now proceed\n';
  script += '# ============================================\n\n';
  
  // BLOCK 2.5: WAN Interface Configuration
  // Support both new WANConfig and legacy fields
  const hasWan1 = input.wan1?.enabled || input.wan1_type;
  const hasWan2 = (input.wan2?.enabled) || (input.wan2_enabled && input.wan2_type);
  
  // Determine WAN interface names for SD-WAN
  let wan1InterfaceName = 'wan1';
  let wan2InterfaceName = 'wan2';
  
  if (hasWan1) {
    script += '# ============================================\n';
    script += '# BLOCK 2.5: WAN Interface Configuration\n';
    script += '# ============================================\n\n';
    script += 'config system interface\n';
    
    // WAN1 Configuration
    if (input.wan1?.enabled) {
      // NEW: Dynamic WAN Configuration
      const wan1 = input.wan1;
      
      if (wan1.is_vlan && wan1.vlan_id) {
        // WAN IS A VLAN (Tagged Mode)
        wan1InterfaceName = `WAN1_${wan1.vlan_id}`;
        script += `  edit "${wan1InterfaceName}"\n`;
        script += '    set vdom "root"\n';
        script += '    set type vlan\n';
        script += `    set interface "${wan1.physical_port}"\n`;
        script += `    set vlanid ${wan1.vlan_id}\n`;
      } else {
        // WAN IS NOT A VLAN (Access Mode)
        wan1InterfaceName = wan1.physical_port || 'wan1';
        script += `  edit "${wan1InterfaceName}"\n`;
        script += '    set vdom "root"\n';
      }
      
      script += `    set mode ${wan1.type}\n`;
      
      if (wan1.type === 'static') {
        if (wan1.ip && wan1.subnet) {
          script += `    set ip ${wan1.ip} ${wan1.subnet}\n`;
        }
      } else if (wan1.type === 'pppoe') {
        if (wan1.username) {
          script += `    set username "${wan1.username}"\n`;
        }
        if (wan1.password) {
          script += `    set password "${wan1.password}"\n`;
        }
      }
      
      script += '    set allowaccess ping\n';
      script += '    set role wan\n';
      script += '  next\n';
    } else if (input.wan1_type) {
      // LEGACY: Backward compatibility
      script += '  edit "wan1"\n';
      script += '    set vdom "root"\n';
      script += `    set mode ${input.wan1_type}\n`;
      
      if (input.wan1_type === 'static') {
        if (input.wan1_ip && input.wan1_subnet) {
          script += `    set ip ${input.wan1_ip} ${input.wan1_subnet}\n`;
        }
      } else if (input.wan1_type === 'pppoe') {
        if (input.wan1_username) {
          script += `    set username "${input.wan1_username}"\n`;
        }
        if (input.wan1_password) {
          script += `    set password "${input.wan1_password}"\n`;
        }
      }
      
      script += '    set allowaccess ping\n';
      script += '    set role wan\n';
      script += '  next\n';
    }
    
    // WAN2 Configuration (if enabled)
    if (input.wan2?.enabled) {
      const wan2 = input.wan2;
      
      if (wan2.is_vlan && wan2.vlan_id) {
        // WAN2 IS A VLAN (Tagged Mode)
        wan2InterfaceName = `WAN2_${wan2.vlan_id}`;
        script += `  edit "${wan2InterfaceName}"\n`;
        script += '    set vdom "root"\n';
        script += '    set type vlan\n';
        script += `    set interface "${wan2.physical_port}"\n`;
        script += `    set vlanid ${wan2.vlan_id}\n`;
      } else {
        // WAN2 IS NOT A VLAN (Access Mode)
        wan2InterfaceName = wan2.physical_port || 'wan2';
        script += `  edit "${wan2InterfaceName}"\n`;
        script += '    set vdom "root"\n';
      }
      
      script += `    set mode ${wan2.type}\n`;
      
      if (wan2.type === 'static') {
        if (wan2.ip && wan2.subnet) {
          script += `    set ip ${wan2.ip} ${wan2.subnet}\n`;
        }
      } else if (wan2.type === 'pppoe') {
        if (wan2.username) {
          script += `    set username "${wan2.username}"\n`;
        }
        if (wan2.password) {
          script += `    set password "${wan2.password}"\n`;
        }
      }
      
      script += '    set allowaccess ping\n';
      script += '    set role wan\n';
      script += '  next\n';
    } else if (input.wan2_enabled && input.wan2_type) {
      // LEGACY: Backward compatibility
      script += '  edit "wan2"\n';
      script += '    set vdom "root"\n';
      script += `    set mode ${input.wan2_type}\n`;
      
      if (input.wan2_type === 'static') {
        if (input.wan2_ip && input.wan2_subnet) {
          script += `    set ip ${input.wan2_ip} ${input.wan2_subnet}\n`;
        }
      } else if (input.wan2_type === 'pppoe') {
        if (input.wan2_username) {
          script += `    set username "${input.wan2_username}"\n`;
        }
        if (input.wan2_password) {
          script += `    set password "${input.wan2_password}"\n`;
        }
      }
      
      script += '    set allowaccess ping\n';
      script += '    set role wan\n';
      script += '  next\n';
    }
    
    script += 'end\n\n';
    
    // ============================================
    // SD-WAN Configuration (FortiOS 7.2+ Modern Syntax)
    // CRITICAL: config system virtual-wan-link is DEPRECATED in FortiOS 7.0+
    // Use config system sdwan with zones for modern firmware compatibility
    // ============================================
    script += '# ============================================\n';
    script += '# BLOCK 2.6: SD-WAN (FortiOS 7.2+ Syntax)\n';
    script += '# ============================================\n\n';
    script += 'config system sdwan\n';
    script += '  set status enable\n';
    
    // Create SD-WAN zone
    script += '  config zone\n';
    script += '    edit "virtual-wan-link"\n';
    script += '    next\n';
    script += '  end\n';
    
    // Configure SD-WAN members
    script += '  config members\n';
    script += '    edit 1\n';
    script += `      set interface "${wan1InterfaceName}"\n`;
    script += '      set zone "virtual-wan-link"\n';
    
    // Set gateway for static WANs
    const wan1Gateway = input.wan1?.gateway || input.wan1_gateway;
    const wan1Type = input.wan1?.type || input.wan1_type;
    if (wan1Type === 'static' && wan1Gateway) {
      script += `      set gateway ${wan1Gateway}\n`;
    }
    script += '    next\n';
    
    if (hasWan2) {
      script += '    edit 2\n';
      script += `      set interface "${wan2InterfaceName}"\n`;
      script += '      set zone "virtual-wan-link"\n';
      
      const wan2Gateway = input.wan2?.gateway || input.wan2_gateway;
      const wan2Type = input.wan2?.type || input.wan2_type;
      if (wan2Type === 'static' && wan2Gateway) {
        script += `      set gateway ${wan2Gateway}\n`;
      }
      script += '    next\n';
    }
    
    script += '  end\n';
    script += 'end\n\n';
    
    // Default Route via SD-WAN Zone (FortiOS 7.2+ syntax)
    script += '# Default Route via SD-WAN Zone\n';
    script += 'config router static\n';
    script += '  edit 1\n';
    script += '    set dst 0.0.0.0 0.0.0.0\n';
    script += '    set sdwan-zone "virtual-wan-link"\n';
    script += '  next\n';
    script += 'end\n\n';
  }
  
  // BLOCK 3: LACP Aggregates
  if (input.lacp_aggregates && input.lacp_aggregates.length > 0) {
    script += '# ============================================\n';
    script += '# BLOCK 3: LACP Aggregates (802.3ad)\n';
    script += '# ============================================\n\n';
    script += 'config system interface\n';
    
    input.lacp_aggregates.forEach((lacp) => {
      const memberPorts = lacp.member_ports.map(p => `"${p}"`).join(' ');
      script += `  edit "${lacp.name}"\n`;
      script += '    set vdom "root"\n';
      script += '    set type aggregate\n';
      script += `    set member ${memberPorts}\n`;
      script += '    set lacp-mode active\n';
      
      // Native Management Network on LACP Parent Interface (only if this LACP is selected)
      if (input.managementNetwork?.enabled && input.managementNetwork.interface === lacp.name) {
        script += '    # Native Management Network (Untagged)\n';
        script += `    set ip ${input.managementNetwork.ipAddress} ${input.managementNetwork.subnetMask}\n`;
        script += '    set allowaccess ping https ssh http fgfm\n';
        script += '    set role lan\n';
      } else if (lacp.has_native_ip && lacp.native_ip && lacp.native_subnet) {
        // Legacy: Add Native IP if configured via old method
        script += `    set ip ${lacp.native_ip} ${lacp.native_subnet}\n`;
        script += '    set allowaccess ping https ssh\n';
      }
      
      script += '  next\n';
    });
    
    script += 'end\n\n';
    
    // DHCP Server for Native Management Network (on LACP parent)
    if (input.managementNetwork?.enabled && input.managementNetwork.dhcpEnabled && 
        input.lacp_aggregates.some(l => l.name === input.managementNetwork?.interface)) {
      const lacpName = input.managementNetwork.interface;
      const mgmtIp = input.managementNetwork.ipAddress;
      const ipParts = mgmtIp.split('.');
      const dhcpStart = input.managementNetwork.dhcpStart || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
      const dhcpEnd = input.managementNetwork.dhcpEnd || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.200`;
      
      script += '# DHCP Server for Native Management Network\n';
      script += 'config system dhcp server\n';
      script += '  edit 99\n';
      script += '    set status enable\n';
      script += '    set dns-service local\n';
      script += `    set default-gateway ${mgmtIp}\n`;
      script += `    set interface "${lacpName}"\n`;
      script += '    config ip-range\n';
      script += '      edit 1\n';
      script += `        set start-ip ${dhcpStart}\n`;
      script += `        set end-ip ${dhcpEnd}\n`;
      script += '      next\n';
      script += '    end\n';
      script += '  next\n';
      script += 'end\n\n';
    }
  }
  
  // BLOCK 3b: Native Management Network on Standalone Physical Port
  if (input.managementNetwork?.enabled && input.managementNetwork.interface && 
      !input.lacp_aggregates?.some(l => l.name === input.managementNetwork?.interface)) {
    const portName = input.managementNetwork.interface;
    const mgmtIp = input.managementNetwork.ipAddress;
    const mgmtSubnet = input.managementNetwork.subnetMask;
    
    script += '# ============================================\n';
    script += '# BLOCK 3b: Native Management Network (Standalone Port)\n';
    script += '# ============================================\n\n';
    
    script += 'config system interface\n';
    script += `  edit "${portName}"\n`;
    script += '    set vdom "root"\n';
    script += '    set mode static\n';
    script += `    set ip ${mgmtIp} ${mgmtSubnet}\n`;
    script += '    set allowaccess ping https ssh http fgfm\n';
    script += '    set role lan\n';
    script += '    set description "Native Management Network"\n';
    script += '  next\n';
    script += 'end\n\n';
    
    // DHCP Server for Native Management Network on Standalone Port
    if (input.managementNetwork.dhcpEnabled) {
      const ipParts = mgmtIp.split('.');
      const dhcpStart = input.managementNetwork.dhcpStart || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
      const dhcpEnd = input.managementNetwork.dhcpEnd || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.200`;
      
      script += '# DHCP Server for Native Management Network (Standalone Port)\n';
      script += 'config system dhcp server\n';
      script += '  edit 99\n';
      script += '    set status enable\n';
      script += '    set dns-service local\n';
      script += `    set default-gateway ${mgmtIp}\n`;
      script += `    set interface "${portName}"\n`;
      script += '    config ip-range\n';
      script += '      edit 1\n';
      script += `        set start-ip ${dhcpStart}\n`;
      script += `        set end-ip ${dhcpEnd}\n`;
      script += '      next\n';
      script += '    end\n';
      script += '  next\n';
      script += 'end\n\n';
    }
  }
  
  // BLOCK 4: Address Objects & Groups
  if (input.vlans.length > 0) {
    script += '# ============================================\n';
    script += '# BLOCK 4: Address Objects & Groups\n';
    script += '# ============================================\n\n';
    script += 'config firewall address\n';
    
    const vlanObjectNames: string[] = [];
    
    input.vlans.forEach((vlan) => {
      const objName = `OBJ_${vlan.nome_vlan}`;
      vlanObjectNames.push(objName);
      script += `  edit "${objName}"\n`;
      script += '    set type ipmask\n';
      script += `    set subnet ${vlan.network_ip} ${vlan.subnet_mask}\n`;
      script += '  next\n';
    });
    
    script += 'end\n\n';
    
    script += 'config firewall addrgrp\n';
    script += '  edit "GRP_INTERNAL_NETWORKS"\n';
    script += `    set member ${vlanObjectNames.map(n => `"${n}"`).join(' ')}\n`;
    script += '  next\n';
    script += 'end\n\n';
    
    // BLOCK 5: VLAN Interfaces
    script += '# ============================================\n';
    script += '# BLOCK 5: VLAN Interfaces\n';
    script += '# ============================================\n\n';
    script += 'config system interface\n';
    
    input.vlans.forEach((vlan) => {
      // The base interface is whatever the user selected in the UI
      // It could be:
      // 1. An LACP aggregate name (e.g., "Core_Trunk")
      // 2. A physical port NOT in any LACP (e.g., "port5")
      const baseInterface = vlan.porte_assegnate[0];
      
      // SECURITY-FIRST: Determine access level based on VLAN type
      const securityTier = getVlanSecurityTier(vlan.vlan_type, vlan.nome_vlan);
      const allowAccessLine = getFortinetAllowAccess(securityTier, false);
      
      script += `  # VLAN: ${vlan.nome_vlan} [Security Tier: ${securityTier.toUpperCase()}]\n`;
      script += `  edit "${vlan.nome_vlan}"\n`;
      script += '    set vdom "root"\n';
      script += `    set ip ${vlan.gateway_ip} ${vlan.subnet_mask}\n`;
      // Only add allowaccess line if the VLAN is trusted or management
      if (allowAccessLine) {
        script += allowAccessLine;
      } else {
        script += '    # SECURITY: No management access for untrusted network\n';
      }
      script += '    set type vlan\n';
      script += `    set interface "${baseInterface}"\n`;
      script += `    set vlanid ${vlan.vlan_id}\n`;
      script += '  next\n';
    });
    
    script += 'end\n\n';
    
    // BLOCK 5.5: DHCP Servers
    const dhcpVlans = input.vlans.filter(v => v.enable_dhcp);
    if (dhcpVlans.length > 0) {
      script += '# ============================================\n';
      script += '# BLOCK 5.5: DHCP Servers\n';
      script += '# ============================================\n\n';
      script += 'config system dhcp server\n';
      
      dhcpVlans.forEach((vlan) => {
        script += '  edit 0\n';
        script += `    set interface "${vlan.nome_vlan}"\n`;
        script += `    set netmask ${vlan.subnet_mask}\n`;
        script += `    set default-gateway ${vlan.gateway_ip}\n`;
        
        // Use dynamic DHCP range if provided, otherwise fall back to calculated
        let startIP = vlan.dhcp_start;
        let endIP = vlan.dhcp_end;
        
        if (!startIP || !endIP) {
          // Calculate safe defaults based on subnet
          const ipParts = vlan.gateway_ip.split('.');
          startIP = `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
          endIP = `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.250`;
        }
        
        script += '    config ip-range\n';
        script += '      edit 1\n';
        script += `        set start-ip ${startIP}\n`;
        script += `        set end-ip ${endIP}\n`;
        script += '      next\n';
        script += '    end\n';
        script += '  next\n';
      });
      
      script += 'end\n\n';
    }
  }
  
  // BLOCK 6: UTM Profiles (WebFilter)
  if (input.webfilter_config) {
    const wf = input.webfilter_config;
    const hasWebFilter = wf.block_adult || wf.block_malicious || wf.block_social || wf.block_gambling || wf.block_proxy;
    
    if (hasWebFilter) {
      script += '# ============================================\n';
      script += '# BLOCK 6: UTM Profiles (WebFilter)\n';
      script += '# ============================================\n\n';
      script += 'config webfilter profile\n';
      
      // Corporate WebFilter Profile
      script += '  edit "WF_CORPORATE"\n';
      script += '    config ftgd-wf\n';
      script += '      config filters\n';
      
      let filterIndex = 1;
      if (wf.block_adult) {
        script += `        edit ${filterIndex++}\n`;
        script += '          set category 11\n';
        script += '          set action block\n';
        script += '        next\n';
      }
      if (wf.block_malicious) {
        // Malicious Websites (26) and Phishing (61)
        script += `        edit ${filterIndex++}\n`;
        script += '          set category 26\n';
        script += '          set action block\n';
        script += '        next\n';
        script += `        edit ${filterIndex++}\n`;
        script += '          set category 61\n';
        script += '          set action block\n';
        script += '        next\n';
      }
      if (wf.block_social) {
        script += `        edit ${filterIndex++}\n`;
        script += '          set category 37\n';
        script += '          set action block\n';
        script += '        next\n';
      }
      if (wf.block_gambling) {
        script += `        edit ${filterIndex++}\n`;
        script += '          set category 6\n';
        script += '          set action block\n';
        script += '        next\n';
      }
      if (wf.block_proxy) {
        script += `        edit ${filterIndex++}\n`;
        script += '          set category 56\n';
        script += '          set action block\n';
        script += '        next\n';
      }
      
      script += '      end\n';
      script += '    end\n';
      script += '  next\n';
      
      // Guest WebFilter Profile (stricter)
      script += '  edit "WF_GUEST"\n';
      script += '    config ftgd-wf\n';
      script += '      config filters\n';
      script += '        edit 1\n';
      script += '          set category 11\n';
      script += '          set action block\n';
      script += '        next\n';
      script += '        edit 2\n';
      script += '          set category 26\n';
      script += '          set action block\n';
      script += '        next\n';
      script += '        edit 3\n';
      script += '          set category 61\n';
      script += '          set action block\n';
      script += '        next\n';
      script += '      end\n';
      script += '    end\n';
      script += '  next\n';
      script += 'end\n\n';
    }
  }
  
  // --- APP REACHABILITY BLOCK (L7/ISDB) ---
  // CRITICAL: Must come BEFORE Smart Policies and BEFORE CORP_TO_INTERNET!
  script += generateAppReachabilityBlock(input.vlans, input.app_reachability);
  
  // --- SMART POLICIES BLOCK (Must come BEFORE general firewall policies) ---
  script += generateSmartPoliciesBlock(input.vlans, input.smart_policies);
  
  // BLOCK 7: Firewall Policies
  if ((input.policies && input.policies.length > 0) || input.wan1_type || input.vlans.length > 0) {
    script += '# ============================================\n';
    script += '# BLOCK 7: Firewall Policies\n';
    script += '# ============================================\n\n';
    script += 'config firewall policy\n';
    
    // Determine Corporate and Guest VLANs
    const corporateVlans = input.vlans.filter(v => v.vlan_type === 'corporate' || v.vlan_type === 'server' || !v.vlan_type);
    const guestVlans = input.vlans.filter(v => v.vlan_type === 'guest');
    
    // Build outbound services list
    let services = '"HTTP" "HTTPS" "DNS"';
    if (input.outbound_services) {
      const svc = input.outbound_services;
      if (svc.imaps) services += ' "IMAPS"';
      if (svc.smtps) services += ' "SMTPS"';
      if (svc.ftp) services += ' "FTP"';
      if (svc.ntp) services += ' "NTP"';
    }
    
    // Corporate Outbound Policy with UTM
    if (input.wan1_type && corporateVlans.length > 0) {
      const vlanNames = corporateVlans.map(v => `"${v.nome_vlan}"`).join(' ');
      const srcAddrs = corporateVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
      
      script += '  # Corporate Outbound Policy\n';
      script += '  edit 0\n';
      script += '    set name "CORP_TO_INTERNET"\n';
      script += `    set srcintf ${vlanNames}\n`;
      script += '    set dstintf "virtual-wan-link"\n';
      script += `    set srcaddr ${srcAddrs}\n`;
      script += '    set dstaddr "all"\n';
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += `    set service ${services}\n`;
      
      // UTM Settings
      if (input.webfilter_config && (input.webfilter_config.block_adult || input.webfilter_config.block_malicious)) {
        script += '    set utm-status enable\n';
        script += '    set ssl-ssh-profile "certificate-inspection"\n';
        script += '    set av-profile "default"\n';
        script += '    set webfilter-profile "WF_CORPORATE"\n';
        script += '    set application-list "default"\n';
        script += '    set ips-sensor "default"\n';
      }
      
      script += '    set logtraffic all\n';
      script += '    set nat enable\n';
      script += '  next\n';
    }
    
    // Guest Outbound Policy with UTM
    if (input.wan1_type && guestVlans.length > 0) {
      const vlanNames = guestVlans.map(v => `"${v.nome_vlan}"`).join(' ');
      const srcAddrs = guestVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
      
      script += '  # Guest Outbound Policy\n';
      script += '  edit 0\n';
      script += '    set name "GUEST_TO_INTERNET"\n';
      script += `    set srcintf ${vlanNames}\n`;
      script += '    set dstintf "virtual-wan-link"\n';
      script += `    set srcaddr ${srcAddrs}\n`;
      script += '    set dstaddr "all"\n';
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += '    set service "ALL"\n';
      script += '    set utm-status enable\n';
      script += '    set ssl-ssh-profile "certificate-inspection"\n';
      script += '    set av-profile "default"\n';
      script += '    set webfilter-profile "WF_GUEST"\n';
      script += '    set logtraffic all\n';
      script += '    set nat enable\n';
      script += '  next\n';
    }
    
    // Fallback: Simple outbound if no VLAN types specified
    if (input.wan1_type && input.vlans.length > 0 && corporateVlans.length === 0 && guestVlans.length === 0) {
      const vlanNames = input.vlans.map(v => `"${v.nome_vlan}"`).join(' ');
      script += '  # Outbound Internet Policy\n';
      script += '  edit 0\n';
      script += '    set name "OUTBOUND_INTERNET"\n';
      script += `    set srcintf ${vlanNames}\n`;
      script += '    set dstintf "virtual-wan-link"\n';
      script += '    set srcaddr "GRP_INTERNAL_NETWORKS"\n';
      script += '    set dstaddr "all"\n';
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += '    set service "ALL"\n';
      script += '    set nat enable\n';
      script += '    set logtraffic all\n';
      script += '  next\n';
    }
    
    // LAN to Server AD Policy
    if (input.policies.includes('lan_to_server') && input.policy_config.ad_source_vlan && input.policy_config.ad_dest_vlan) {
      script += '  # LAN to AD Server Policy\n';
      script += '  edit 0\n';
      script += '    set name "LAN_TO_AD"\n';
      script += `    set srcintf "${input.policy_config.ad_source_vlan}"\n`;
      script += `    set dstintf "${input.policy_config.ad_dest_vlan}"\n`;
      script += `    set srcaddr "OBJ_${input.policy_config.ad_source_vlan}"\n`;
      script += `    set dstaddr "OBJ_${input.policy_config.ad_dest_vlan}"\n`;
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += '    set service "LDAP" "LDAP_UDP" "SMB" "DNS" "RDP"\n';
      script += '    set nat disable\n';
      script += '  next\n';
    }
    
    script += 'end\n\n';
  }
  
  // --- IoT Zero-Trust Configuration ---
  script += generateIoTZeroTrustBlock(input.vlans, input.outbound_services);
  
  script += '# ============================================\n';
  script += '# Script Generation Complete\n';
  script += '# Ready for Console Deployment\n';
  script += '# ============================================\n';
  
  return script;
};

function getDefaultSwitchName(modello: string): string {
  // HAL-compliant: Use hardware dictionary for switch name
  const hardwareModel = getModelByName(modello);
  if (hardwareModel?.hardwareSwitchName) {
    return hardwareModel.hardwareSwitchName;
  }
  
  // Fallback for legacy models not in dictionary
  const switchMap: Record<string, string> = {
    '40F': 'internal',
    '60F': 'internal',
    '80F': 'internal',
    '90G': 'internal',
    '100F': 'internal', // 100F doesn't have hardware switch, but fallback
    '30G': 'lan',       // VERIFIED from real factory config
    '60G': 'internal',
    '120G': 'internal',
  };
  return switchMap[modello] || 'internal';
}

/**
 * Generate App Reachability (L7/FQDN/ISDB) Firewall Policies for FortiOS
 * 
 * CRITICAL: These rules MUST be placed BEFORE the general CORP_TO_INTERNET policy!
 * FortiGate evaluates policies top-down, so specific L7 blocks must come first.
 * 
 * FIX 2: Now supports dual-action (blocked_vlans + allowed_vlans arrays)
 * Uses Fortinet ISDB (Internet Service Database) for precise app identification.
 * ISDB Names are HARDCODED and MUST NOT be modified - they are vendor-specific!
 */
function generateAppReachabilityBlock(vlans: VLANInput[], appReachability?: AppReachabilityConfig): string {
  if (!appReachability?.enabled || !appReachability.rules || appReachability.rules.length === 0) {
    return '';
  }
  
  let script = '';
  
  script += '# ============================================\n';
  script += '# BLOCK APP-L7: App & Social Reachability Rules\n';
  script += '# CRITICAL: These rules MUST be BEFORE CORP_TO_INTERNET!\n';
  script += '# ============================================\n\n';
  
  script += 'config firewall policy\n';
  
  // Process each rule with dual-action support
  appReachability.rules.forEach((rule, ruleIndex) => {
    const appDef = getAppDefinition(rule.appId);
    if (!appDef) {
      console.warn(`[AppReachability] Unknown app ID: ${rule.appId}`);
      return;
    }
    
    // FIX: Consolidate all ISDB names into a single string
    // FortiOS supports: set internet-service-name "ISDB1" "ISDB2" "ISDB3"
    const isdbNamesStr = appDef.fortinetIsdb.map(name => `"${name}"`).join(' ');
    
    // Process BLOCKED VLANs (deny policies) - ONE policy per app
    if (rule.blocked_vlans && rule.blocked_vlans.length > 0) {
      const blockedVlanObjs = vlans.filter(v => rule.blocked_vlans.includes(v.nome_vlan));
      if (blockedVlanObjs.length > 0) {
        const vlanNames = blockedVlanObjs.map(v => `"${v.nome_vlan}"`).join(' ');
        const srcAddrs = blockedVlanObjs.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
        
        script += `  # [L7-BLOCK] ${appDef.name} (${rule.blocked_vlans.join(', ')})\n`;
        script += '  edit 0\n';
        script += `    set name "APP_BLOCK_${appDef.id.toUpperCase()}_${ruleIndex}"\n`;
        script += `    set srcintf ${vlanNames}\n`;
        script += '    set dstintf "virtual-wan-link"\n';
        script += `    set srcaddr ${srcAddrs}\n`;
        script += '    set dstaddr "all"\n';
        script += '    set action deny\n';
        script += '    set schedule "always"\n';
        script += '    set service "ALL"\n';
        script += '    # ISDB: Fortinet Internet Service Database (consolidated)\n';
        script += '    set internet-service enable\n';
        script += `    set internet-service-name ${isdbNamesStr}\n`;
        script += '    set logtraffic all\n';
        script += `    set comments "App Reachability: BLOCK ${appDef.name} for ${rule.blocked_vlans.join(', ')}"\n`;
        script += '  next\n';
      }
    }
    
    // Process ALLOWED VLANs (accept policies) - ONE policy per app
    if (rule.allowed_vlans && rule.allowed_vlans.length > 0) {
      const allowedVlanObjs = vlans.filter(v => rule.allowed_vlans.includes(v.nome_vlan));
      if (allowedVlanObjs.length > 0) {
        const vlanNames = allowedVlanObjs.map(v => `"${v.nome_vlan}"`).join(' ');
        const srcAddrs = allowedVlanObjs.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
        
        script += `  # [L7-ALLOW] ${appDef.name} (${rule.allowed_vlans.join(', ')})\n`;
        script += '  edit 0\n';
        script += `    set name "APP_ALLOW_${appDef.id.toUpperCase()}_${ruleIndex}"\n`;
        script += `    set srcintf ${vlanNames}\n`;
        script += '    set dstintf "virtual-wan-link"\n';
        script += `    set srcaddr ${srcAddrs}\n`;
        script += '    set dstaddr "all"\n';
        script += '    set action accept\n';
        script += '    set schedule "always"\n';
        script += '    set service "ALL"\n';
        script += '    # ISDB: Fortinet Internet Service Database (consolidated)\n';
        script += '    set internet-service enable\n';
        script += `    set internet-service-name ${isdbNamesStr}\n`;
        script += '    set nat enable\n';
        script += '    set logtraffic all\n';
        script += `    set comments "App Reachability: ALLOW ${appDef.name} for ${rule.allowed_vlans.join(', ')}"\n`;
        script += '  next\n';
      }
    }
  });
  
  script += 'end\n\n';
  
  return script;
}

/**
 * Generate Smart Policies Service Objects and Firewall Rules for FortiOS
 * 
 * CRITICAL: DROP rules (Priority 1) MUST be generated BEFORE ALLOW rules (Priority 2)
 * This ensures traffic is blocked before any permissive rules are evaluated.
 */
function generateSmartPoliciesBlock(vlans: VLANInput[], smartPolicies?: SmartPoliciesConfig): string {
  if (!smartPolicies) {
    return '';
  }
  
  // Check if any policy is enabled
  const hasAnyPolicy = 
    smartPolicies.policy_block_high_risk ||
    smartPolicies.policy_isolate_guest ||
    smartPolicies.policy_admin_omnipresence ||
    smartPolicies.policy_voip_outbound ||
    smartPolicies.policy_allow_printers ||
    smartPolicies.policy_allow_casting ||
    smartPolicies.policy_vendor_ot;
    
  if (!hasAnyPolicy) {
    return '';
  }
  
  let script = '';
  
  script += '# ============================================\n';
  script += '# BLOCK SMART: Smart Policy Builder Rules\n';
  script += '# ============================================\n\n';
  
  // Resolve VLANs by keyword matching
  const adminVlans = findVlansByKeywords(vlans, VLAN_KEYWORDS.admin);
  const staffVlans = findVlansByKeywords(vlans, VLAN_KEYWORDS.staff);
  const guestVlans = findVlansByKeywords(vlans, VLAN_KEYWORDS.guest);
  const printerVlan = findVlanByKeywords(vlans, VLAN_KEYWORDS.printer);
  const voipVlans = findVlansByKeywords(vlans, VLAN_KEYWORDS.voip);
  const iotVlans = findVlansByKeywords(vlans, VLAN_KEYWORDS.iot);
  const otVlan = findVlanByKeywords(vlans, VLAN_KEYWORDS.ot);
  const serverVlans = findVlansByKeywords(vlans, VLAN_KEYWORDS.servers);
  
  // DEBUG: Log resolved VLANs (can be removed in production)
  console.log('[SmartPolicies] Resolved VLANs:', {
    admin: adminVlans.map(v => v.nome_vlan),
    staff: staffVlans.map(v => v.nome_vlan),
    guest: guestVlans.map(v => v.nome_vlan),
    printer: printerVlan?.nome_vlan,
    voip: voipVlans.map(v => v.nome_vlan),
    iot: iotVlans.map(v => v.nome_vlan),
    ot: otVlan?.nome_vlan,
    servers: serverVlans.map(v => v.nome_vlan),
  });
  
  // ============================================
  // SERVICE OBJECTS
  // ============================================
  script += '# --- Smart Policy Service Objects ---\n';
  script += 'config firewall service custom\n';
  
  // SVC_PRINTERS: TCP 9100 (RAW), TCP 631 (IPP), TCP 515 (LPD)
  if (smartPolicies.policy_allow_printers) {
    script += '  edit "SVC_PRINTERS"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set tcp-portrange 9100 631 515\n';
    script += '    set comment "Printer Ports: RAW(9100), IPP(631), LPD(515)"\n';
    script += '  next\n';
  }
  
  // SVC_CASTING: mDNS, AirPlay, Chromecast
  if (smartPolicies.policy_allow_casting) {
    script += '  edit "SVC_CASTING_TCP"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set tcp-portrange 7000 7100 8008 8009\n';
    script += '    set comment "Casting TCP: AirPlay(7000,7100), Chromecast(8008,8009)"\n';
    script += '  next\n';
    script += '  edit "SVC_CASTING_UDP"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set udp-portrange 5353\n';
    script += '    set comment "mDNS/Bonjour Discovery"\n';
    script += '  next\n';
  }
  
  // SVC_VOIP: SIP + RTP
  if (smartPolicies.policy_voip_outbound) {
    script += '  edit "SVC_VOIP_SIP"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set udp-portrange 5060-5061\n';
    script += '    set comment "SIP Signaling"\n';
    script += '  next\n';
    script += '  edit "SVC_VOIP_RTP"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set udp-portrange 10000-64999\n';
    script += '    set comment "RTP Media Stream"\n';
    script += '  next\n';
  }
  
  // SVC_HIGH_RISK: SMB, RDP, SQL
  if (smartPolicies.policy_block_high_risk) {
    script += '  edit "SVC_HIGH_RISK_TCP"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set tcp-portrange 445 3389 1433 3306\n';
    script += '    set comment "High Risk: SMB(445), RDP(3389), MSSQL(1433), MySQL(3306)"\n';
    script += '  next\n';
    script += '  edit "SVC_HIGH_RISK_UDP"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set udp-portrange 445\n';
    script += '    set comment "High Risk: SMB/UDP(445)"\n';
    script += '  next\n';
  }
  
  // SVC_OT_HVAC: BACnet, Modbus
  if (smartPolicies.policy_vendor_ot) {
    script += '  edit "SVC_OT_HVAC"\n';
    script += '    set protocol TCP/UDP/SCTP\n';
    script += '    set tcp-portrange 502\n';
    script += '    set udp-portrange 47808\n';
    script += '    set comment "OT/HVAC: BACnet(47808), Modbus(502)"\n';
    script += '  next\n';
  }
  
  script += 'end\n\n';
  
  // Service Groups
  script += 'config firewall service group\n';
  
  if (smartPolicies.policy_allow_casting) {
    script += '  edit "GRP_SVC_CASTING"\n';
    script += '    set member "SVC_CASTING_TCP" "SVC_CASTING_UDP"\n';
    script += '  next\n';
  }
  
  if (smartPolicies.policy_voip_outbound) {
    script += '  edit "GRP_SVC_VOIP"\n';
    script += '    set member "SVC_VOIP_SIP" "SVC_VOIP_RTP"\n';
    script += '  next\n';
  }
  
  if (smartPolicies.policy_block_high_risk) {
    script += '  edit "GRP_SVC_HIGH_RISK"\n';
    script += '    set member "SVC_HIGH_RISK_TCP" "SVC_HIGH_RISK_UDP"\n';
    script += '  next\n';
  }
  
  script += 'end\n\n';
  
  // RFC1918 Address Object for Guest Isolation
  if (smartPolicies.policy_isolate_guest && guestVlans.length > 0) {
    script += '# RFC1918 Private Networks for Guest Isolation\n';
    script += 'config firewall address\n';
    script += '  edit "RFC1918_10"\n';
    script += '    set type ipmask\n';
    script += '    set subnet 10.0.0.0 255.0.0.0\n';
    script += '  next\n';
    script += '  edit "RFC1918_172"\n';
    script += '    set type ipmask\n';
    script += '    set subnet 172.16.0.0 255.240.0.0\n';
    script += '  next\n';
    script += '  edit "RFC1918_192"\n';
    script += '    set type ipmask\n';
    script += '    set subnet 192.168.0.0 255.255.0.0\n';
    script += '  next\n';
    script += 'end\n\n';
    
    script += 'config firewall addrgrp\n';
    script += '  edit "GRP_RFC1918"\n';
    script += '    set member "RFC1918_10" "RFC1918_172" "RFC1918_192"\n';
    script += '    set comment "All Private Networks"\n';
    script += '  next\n';
    script += 'end\n\n';
  }
  
  // ============================================
  // FIREWALL POLICIES
  // ============================================
  script += '# --- Smart Policy Firewall Rules ---\n';
  script += '# PRIORITY ORDER: DROP rules first, then ALLOW rules\n';
  script += 'config firewall policy\n';
  
  // ============================================
  // PRIORITY 1: DROP RULES (MUST COME FIRST!)
  // ============================================
  
  // 1a. Block High-Risk Protocols to WAN
  if (smartPolicies.policy_block_high_risk && vlans.length > 0) {
    const allVlanNames = vlans.map(v => `"${v.nome_vlan}"`).join(' ');
    
    script += '  # [P1-DROP] Block High-Risk Protocols to Internet\n';
    script += '  edit 0\n';
    script += '    set name "SP_BLOCK_HIGH_RISK_OUT"\n';
    script += `    set srcintf ${allVlanNames}\n`;
    script += '    set dstintf "virtual-wan-link"\n';
    script += '    set srcaddr "all"\n';
    script += '    set dstaddr "all"\n';
    script += '    set action deny\n';
    script += '    set schedule "always"\n';
    script += '    set service "GRP_SVC_HIGH_RISK"\n';
    script += '    set logtraffic all\n';
    script += '    set comments "Smart Policy: Block SMB/RDP/SQL egress"\n';
    script += '  next\n';
  }
  
  // 1b. Isolate Guest from RFC1918 (Internal Networks)
  if (smartPolicies.policy_isolate_guest && guestVlans.length > 0) {
    const guestVlanNames = guestVlans.map(v => `"${v.nome_vlan}"`).join(' ');
    const guestAddrObjs = guestVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
    const nonGuestVlans = vlans.filter(v => !guestVlans.includes(v));
    
    if (nonGuestVlans.length > 0) {
      const nonGuestVlanNames = nonGuestVlans.map(v => `"${v.nome_vlan}"`).join(' ');
      
      script += '  # [P1-DROP] Guest Isolation - Block to Internal Networks\n';
      script += '  edit 0\n';
      script += '    set name "SP_ISOLATE_GUEST_RFC1918"\n';
      script += `    set srcintf ${guestVlanNames}\n`;
      script += `    set dstintf ${nonGuestVlanNames}\n`;
      script += `    set srcaddr ${guestAddrObjs}\n`;
      script += '    set dstaddr "GRP_RFC1918"\n';
      script += '    set action deny\n';
      script += '    set schedule "always"\n';
      script += '    set service "ALL"\n';
      script += '    set logtraffic all\n';
      script += '    set comments "Smart Policy: Guest cannot reach internal networks"\n';
      script += '  next\n';
    }
    
    // Also block Guest to Guest (inter-client isolation)
    script += '  # [P1-DROP] Guest Inter-Client Isolation\n';
    script += '  edit 0\n';
    script += '    set name "SP_ISOLATE_GUEST_INTER"\n';
    script += `    set srcintf ${guestVlanNames}\n`;
    script += `    set dstintf ${guestVlanNames}\n`;
    script += `    set srcaddr ${guestAddrObjs}\n`;
    script += `    set dstaddr ${guestAddrObjs}\n`;
    script += '    set action deny\n';
    script += '    set schedule "always"\n';
    script += '    set service "ALL"\n';
    script += '    set logtraffic all\n';
    script += '    set comments "Smart Policy: Guest clients cannot communicate with each other"\n';
    script += '  next\n';
  }
  
  // ============================================
  // PRIORITY 2: ALLOW RULES (After DROP rules)
  // ============================================
  
  // 2a. Admin Omnipresence - Admin to ALL
  if (smartPolicies.policy_admin_omnipresence && adminVlans.length > 0) {
    const adminVlanNames = adminVlans.map(v => `"${v.nome_vlan}"`).join(' ');
    const adminAddrObjs = adminVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
    const allOtherVlans = vlans.filter(v => !adminVlans.includes(v));
    
    if (allOtherVlans.length > 0) {
      const allOtherVlanNames = allOtherVlans.map(v => `"${v.nome_vlan}"`).join(' ');
      
      script += '  # [P2-ALLOW] Admin Omnipresence - Full Access\n';
      script += '  edit 0\n';
      script += '    set name "SP_ADMIN_OMNIPRESENCE"\n';
      script += `    set srcintf ${adminVlanNames}\n`;
      script += `    set dstintf ${allOtherVlanNames}\n`;
      script += `    set srcaddr ${adminAddrObjs}\n`;
      script += '    set dstaddr "all"\n';
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += '    set service "ALL"\n';
      script += '    set logtraffic all\n';
      script += '    set comments "Smart Policy: Admin has full access to all networks"\n';
      script += '  next\n';
    }
  }
  
  // 2b. VoIP Outbound
  if (smartPolicies.policy_voip_outbound && voipVlans.length > 0) {
    const voipVlanNames = voipVlans.map(v => `"${v.nome_vlan}"`).join(' ');
    const voipAddrObjs = voipVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
    
    script += '  # [P2-ALLOW] VoIP Outbound - SIP/RTP to Internet\n';
    script += '  edit 0\n';
    script += '    set name "SP_VOIP_OUTBOUND"\n';
    script += `    set srcintf ${voipVlanNames}\n`;
    script += '    set dstintf "virtual-wan-link"\n';
    script += `    set srcaddr ${voipAddrObjs}\n`;
    script += '    set dstaddr "all"\n';
    script += '    set action accept\n';
    script += '    set schedule "always"\n';
    script += '    set service "GRP_SVC_VOIP"\n';
    script += '    set nat enable\n';
    script += '    set logtraffic all\n';
    script += '    set comments "Smart Policy: VoIP phones can make/receive calls"\n';
    script += '  next\n';
  }
  
  // 2c. Allow Printers - Staff to Printer VLAN
  if (smartPolicies.policy_allow_printers && staffVlans.length > 0 && printerVlan) {
    const staffVlanNames = staffVlans.map(v => `"${v.nome_vlan}"`).join(' ');
    const staffAddrObjs = staffVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
    
    script += '  # [P2-ALLOW] Staff to Printers\n';
    script += '  edit 0\n';
    script += '    set name "SP_ALLOW_PRINTERS"\n';
    script += `    set srcintf ${staffVlanNames}\n`;
    script += `    set dstintf "${printerVlan.nome_vlan}"\n`;
    script += `    set srcaddr ${staffAddrObjs}\n`;
    script += `    set dstaddr "OBJ_${printerVlan.nome_vlan}"\n`;
    script += '    set action accept\n';
    script += '    set schedule "always"\n';
    script += '    set service "SVC_PRINTERS"\n';
    script += '    set logtraffic all\n';
    script += '    set comments "Smart Policy: Staff can print to network printers"\n';
    script += '  next\n';
  }
  
  // 2d. Allow Casting - Client/Guest to IoT
  if (smartPolicies.policy_allow_casting && iotVlans.length > 0) {
    // Combine staff and guest as casting sources
    const castingSources = [...staffVlans, ...guestVlans];
    
    if (castingSources.length > 0) {
      const castingVlanNames = castingSources.map(v => `"${v.nome_vlan}"`).join(' ');
      const castingAddrObjs = castingSources.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
      const iotVlanNames = iotVlans.map(v => `"${v.nome_vlan}"`).join(' ');
      const iotAddrObjs = iotVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
      
      script += '  # [P2-ALLOW] Casting - Client/Guest to IoT\n';
      script += '  edit 0\n';
      script += '    set name "SP_ALLOW_CASTING"\n';
      script += `    set srcintf ${castingVlanNames}\n`;
      script += `    set dstintf ${iotVlanNames}\n`;
      script += `    set srcaddr ${castingAddrObjs}\n`;
      script += `    set dstaddr ${iotAddrObjs}\n`;
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += '    set service "GRP_SVC_CASTING"\n';
      script += '    set logtraffic all\n';
      script += '    set comments "Smart Policy: Cast to smart TVs, speakers, etc."\n';
      script += '  next\n';
    }
  }
  
  // 2e. Admin/Management to OT/HVAC (Replaces vendor_ot since we don't have Vendor preset)
  // This allows network administrators to access SCADA/BMS/HVAC systems
  if (smartPolicies.policy_vendor_ot && otVlan) {
    // Use admin VLANs as source (Management network can access OT)
    if (adminVlans.length > 0) {
      const adminVlanNames = adminVlans.map(v => `"${v.nome_vlan}"`).join(' ');
      const adminAddrObjs = adminVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
      
      script += '  # [P2-ALLOW] Admin/Management to OT/HVAC\n';
      script += '  edit 0\n';
      script += '    set name "SP_ADMIN_TO_OT"\n';
      script += `    set srcintf ${adminVlanNames}\n`;
      script += `    set dstintf "${otVlan.nome_vlan}"\n`;
      script += `    set srcaddr ${adminAddrObjs}\n`;
      script += `    set dstaddr "OBJ_${otVlan.nome_vlan}"\n`;
      script += '    set action accept\n';
      script += '    set schedule "always"\n';
      script += '    set service "SVC_OT_HVAC"\n';
      script += '    set logtraffic all\n';
      script += '    set comments "Smart Policy: Admin can access OT/HVAC via BACnet/Modbus"\n';
      script += '  next\n';
    } else {
      // Fallback: If no admin VLAN, allow Staff to OT (less secure but functional)
      if (staffVlans.length > 0) {
        const staffVlanNames = staffVlans.map(v => `"${v.nome_vlan}"`).join(' ');
        const staffAddrObjs = staffVlans.map(v => `"OBJ_${v.nome_vlan}"`).join(' ');
        
        script += '  # [P2-ALLOW] Staff to OT/HVAC (No Admin VLAN found)\n';
        script += '  edit 0\n';
        script += '    set name "SP_STAFF_TO_OT"\n';
        script += `    set srcintf ${staffVlanNames}\n`;
        script += `    set dstintf "${otVlan.nome_vlan}"\n`;
        script += `    set srcaddr ${staffAddrObjs}\n`;
        script += `    set dstaddr "OBJ_${otVlan.nome_vlan}"\n`;
        script += '    set action accept\n';
        script += '    set schedule "always"\n';
        script += '    set service "SVC_OT_HVAC"\n';
        script += '    set logtraffic all\n';
        script += '    set comments "Smart Policy: Staff can access OT/HVAC via BACnet/Modbus"\n';
        script += '  next\n';
      }
    }
  }
  
  script += 'end\n\n';
  
  return script;
}

/**
 * Generate IoT Zero-Trust Objects and Policy
 * Creates FQDN address objects, service objects, and firewall policy for IoT VLANs
 */
function generateIoTZeroTrustBlock(vlans: VLANInput[], outbound_services?: OutboundServicesConfig): string {
  // Check if IoT cloud services are enabled
  if (!outbound_services?.iot_cloud) {
    return '';
  }
  
  // Get IoT VLANs
  const iotVlans = vlans.filter(v => v.vlan_type === 'iot');
  if (iotVlans.length === 0) {
    return '';
  }
  
  let script = '';
  
  script += '# ============================================\n';
  script += '# BLOCK IOT: Zero-Trust IoT/Domotics Configuration\n';
  script += '# ============================================\n\n';
  
  // --- FQDN Address Objects ---
  script += '# --- IoT FQDN Address Objects ---\n';
  script += 'config firewall address\n';
  
  // Generate individual FQDN objects
  let fqdnIndex = 0;
  Object.entries(IOT_FQDN_GROUPS).forEach(([category, fqdns]) => {
    fqdns.forEach(fqdn => {
      fqdnIndex++;
      const objName = `IOT_FQDN_${String(fqdnIndex).padStart(3, '0')}`;
      script += `  edit "${objName}"\n`;
      script += `    set type fqdn\n`;
      script += `    set fqdn "${fqdn}"\n`;
      script += `    set comment "${category.toUpperCase()}"\n`;
      script += '  next\n';
    });
  });
  script += 'end\n\n';
  
  // --- FQDN Address Group ---
  script += '# --- IoT FQDN Address Group ---\n';
  script += 'config firewall addrgrp\n';
  script += '  edit "GRP_FQDN_IOT"\n';
  script += '    set member';
  for (let i = 1; i <= fqdnIndex; i++) {
    script += ` "IOT_FQDN_${String(i).padStart(3, '0')}"`;
  }
  script += '\n';
  script += '    set comment "IoT Smart Home Cloud Endpoints"\n';
  script += '  next\n';
  script += 'end\n\n';
  
  // --- Service Objects ---
  script += '# --- IoT Service Objects (TCP) ---\n';
  script += 'config firewall service custom\n';
  IOT_TCP_PORT_RANGES.forEach((portRange, idx) => {
    const ports = portRange.split('-');
    script += `  edit "IOT_TCP_${portRange.replace('-', '_')}"\n`;
    script += '    set protocol TCP/UDP/SCTP\n';
    script += `    set tcp-portrange ${ports[0]}${ports[1] ? '-' + ports[1] : ''}\n`;
    script += '  next\n';
  });
  script += 'end\n\n';
  
  script += '# --- IoT Service Objects (UDP) ---\n';
  script += 'config firewall service custom\n';
  IOT_UDP_PORT_RANGES.forEach((portRange, idx) => {
    const ports = portRange.split('-');
    script += `  edit "IOT_UDP_${portRange.replace('-', '_')}"\n`;
    script += '    set protocol TCP/UDP/SCTP\n';
    script += `    set udp-portrange ${ports[0]}${ports[1] ? '-' + ports[1] : ''}\n`;
    script += '  next\n';
  });
  script += 'end\n\n';
  
  // --- Service Group ---
  script += '# --- IoT Service Group ---\n';
  script += 'config firewall service group\n';
  script += '  edit "GRP_SRV_IOT"\n';
  script += '    set member';
  IOT_TCP_PORT_RANGES.forEach(p => script += ` "IOT_TCP_${p.replace('-', '_')}"`);
  IOT_UDP_PORT_RANGES.forEach(p => script += ` "IOT_UDP_${p.replace('-', '_')}"`);
  script += '\n';
  script += '    set comment "IoT Smart Home Allowed Ports"\n';
  script += '  next\n';
  script += 'end\n\n';
  
  // --- IoT VLAN Address Objects ---
  script += '# --- IoT VLAN Subnet Address Objects ---\n';
  script += 'config firewall address\n';
  iotVlans.forEach(vlan => {
    script += `  edit "NET_IOT_${vlan.vlan_id}"\n`;
    script += '    set type ipmask\n';
    script += `    set subnet ${vlan.network_ip} ${vlan.subnet_mask}\n`;
    script += `    set comment "IoT VLAN ${vlan.vlan_id} - ${vlan.nome_vlan}"\n`;
    script += '  next\n';
  });
  script += 'end\n\n';
  
  // --- IoT Address Group (Source) ---
  script += '# --- IoT Source Address Group ---\n';
  script += 'config firewall addrgrp\n';
  script += '  edit "GRP_NET_IOT"\n';
  script += '    set member';
  iotVlans.forEach(vlan => script += ` "NET_IOT_${vlan.vlan_id}"`);
  script += '\n';
  script += '    set comment "All IoT VLAN Subnets"\n';
  script += '  next\n';
  script += 'end\n\n';
  
  // --- Firewall Policy: IoT to Internet (Restricted) ---
  script += '# --- IoT Firewall Policy (Zero-Trust) ---\n';
  script += 'config firewall policy\n';
  script += '  edit 0\n';
  script += '    set name "IOT_TO_INTERNET"\n';
  script += '    set srcintf';
  // CRITICAL FIX: Use actual VLAN interface names, not hardcoded "VLAN_X"
  iotVlans.forEach(vlan => script += ` "${vlan.nome_vlan}"`);
  script += '\n';
  script += '    set dstintf "virtual-wan-link"\n';
  script += '    set srcaddr "GRP_NET_IOT"\n';
  script += '    set dstaddr "GRP_FQDN_IOT"\n';
  script += '    set action accept\n';
  script += '    set schedule "always"\n';
  script += '    set service "GRP_SRV_IOT"\n';
  script += '    set nat enable\n';
  script += '    set logtraffic all\n';
  script += '    set comments "Zero-Trust IoT: Allow only authorized cloud services"\n';
  script += '  next\n';
  script += 'end\n\n';
  
  // --- Block Policy: IoT to Internal Networks ---
  const nonIotVlans = vlans.filter(v => v.vlan_type !== 'iot');
  if (nonIotVlans.length > 0) {
    script += '# --- Block IoT to Internal Networks ---\n';
    script += 'config firewall policy\n';
    script += '  edit 0\n';
    script += '    set name "BLOCK_IOT_TO_INTERNAL"\n';
    script += '    set srcintf';
    // CRITICAL FIX: Use actual VLAN interface names
    iotVlans.forEach(vlan => script += ` "${vlan.nome_vlan}"`);
    script += '\n';
    script += '    set dstintf';
    // CRITICAL FIX: Use actual VLAN interface names for destination
    nonIotVlans.forEach(vlan => script += ` "${vlan.nome_vlan}"`);
    script += '\n';
    script += '    set srcaddr "GRP_NET_IOT"\n';
    script += '    set dstaddr "all"\n';
    script += '    set action deny\n';
    script += '    set schedule "always"\n';
    script += '    set service "ALL"\n';
    script += '    set logtraffic all\n';
    script += '    set comments "Zero-Trust: Block IoT from reaching internal VLANs"\n';
    script += '  next\n';
    script += 'end\n\n';
  }
  
  return script;
}
